import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';

import useSavedFilters, { FILTER_SCOPE } from './useSavedFilters';
import SavedFilterPicker from '../components/items/SavedFilterPicker';
import SavedFilterCommit from '../components/items/SavedFilterCommit';
import SavedFilterNameModal from '../components/items/SavedFilterNameModal';
import { confirmDestructive } from '../components/common/confirmDestructive';
import { deriveDataState } from '../components/loading';
import { normalizeId } from '../utils/ids';
import { PICKABLE_REFUSALS } from '../utils/filterValues';
import { DEFAULT_GROUP_TOKEN } from '../utils/itemGroups';
import { writeStoredItemsGroup } from '../utils/itemsGroup';
import { writeStoredBoard } from '../utils/board';
import {
	canonicalText,
	hasSomethingToSave,
	savedFilterBaseline,
	savedFilterChanges,
	savedFilterDraft,
	savedFilterMenuPatch,
	savedFilterStateOf,
	savedFilterStatePatch,
} from '../utils/savedFilterState';

// The element refusals the name window answers by picking (hooks/useElementChoices).
const PICK_ELEMENT_CODES = new Set(['ambiguous_element', 'unknown_element']);

/**
 * THE ITEMS PAGE'S SAVED FILTERS (KPR-1642, KPE-051 rulings i02, i11, i12):
 * which one is applied, whether the screen has changed since, and the two
 * places they show — the menu at the start of row 1 and the commit slot at
 * the end of row 2. It returns `{ menu, commit, savedQueries }` (nodes the
 * toolbar places, as useCombinedScope returns its switch) so the page keeps
 * none of this.
 *
 * - **Applied** is `?saved=<id>` (utils/itemsView). Without it, the filter
 *   whose stored parts are all on screen is named (a filter built again by
 *   hand, a link from before), as the highlighted chip was. An id this person
 *   is not listed (deleted, someone else's own filter) names nothing: nothing
 *   is fetched by id.
 * - **Changed** compares the screen with the filter's stored parts over the
 *   state when it was applied (utils/savedFilterState; the anchor is kept here,
 *   per collection and filter, and taken again on a reload).
 * - **Applying** writes one address patch, the order out loud (`keepDefaults`)
 *   so a table layout's own order never stands in for a stored one; **Revert**
 *   writes the whole baseline; **All items** clears the filter, its text and
 *   its columns. The stored board and group period follow, as they did.
 * - **Save filter**, **Save as new** and **Update** send what is on screen
 *   (`savedFilterDraft`), then apply what the server stored, so the dot goes.
 *
 * `screen` is the page's state as the comparison reads it (savedFilterStateOf's
 * input); `draft` is what a save sends (savedFilterDraft's input).
 */
export default function useSavedFilterControls({
	collectionId,
	savedFilterId,
	canManage = false,
	isMobile = false,
	screen,
	draft: draftInput,
	boardText = '',
	fields = null,
	adoptions = null,
	update,
	onStoredGroup,
}) {
	const { message, modal } = App.useApp();
	const { filters, loading, loadError, loaded, reload, save, rename, update: patchFilter, remove, promote, demote } = useSavedFilters(collectionId);
	const [naming, setNaming] = useState(null);
	const [busy, setBusy] = useState(false);

	// The page hands a new object each render; the state is read again only
	// when what it says moves (the query is parsed to compare it).
	const screenKey = JSON.stringify(screen || {});
	const state = useMemo(() => savedFilterStateOf(JSON.parse(screenKey)), [screenKey]);
	// What a save sends is built when it is asked for, from the latest render.
	const draftInputRef = useRef(draftInput);
	draftInputRef.current = draftInput;
	const draftNow = useCallback(() => savedFilterDraft(draftInputRef.current), []);

	// A filter's text and what the list adopted it as (the F3 review's F4).
	const spellingsOf = useCallback((filter) => {
		const stored = filter?.query;
		if (!stored) return [];
		const adopted = adoptions instanceof Map ? adoptions.get(canonicalText(stored)) ?? adoptions.get(stored) : undefined;
		return adopted && adopted !== stored ? [stored, adopted] : [stored];
	}, [adoptions]);

	// The queries the filters hold, for the builder's recent strip.
	const savedQueries = useMemo(() => filters.flatMap(spellingsOf), [filters, spellingsOf]);

	// ---- Which filter is applied, and the state it was applied over ----
	const byId = savedFilterId ? filters.find(filter => normalizeId(filter?._id) === normalizeId(savedFilterId)) : null;
	const [anchor, setAnchor] = useState(null);
	const anchorKey = byId ? `${collectionId}:${normalizeId(byId._id)}` : null;
	// Taken the first time the filter is known here (a reload, a Home pin's
	// link): what is on screen then is what applying it left.
	if (anchorKey && anchor?.key !== anchorKey) setAnchor({ key: anchorKey, state });
	const anchorState = anchorKey && anchor?.key === anchorKey ? anchor.state : state;

	// No `saved`: the first filter whose stored parts are all on screen.
	const byMatch = useMemo(() => {
		if (byId || !state.q) return null;
		return filters.find(filter => !filter?.pending
			&& !savedFilterChanges(state, savedFilterBaseline(filter, state), { spellings: spellingsOf(filter) }).length) || null;
	}, [byId, filters, state, spellingsOf]);

	const applied = byId || byMatch;
	const baseline = useMemo(
		() => (applied ? savedFilterBaseline(applied, byId ? anchorState : state) : null),
		[applied, byId, anchorState, state]
	);
	const changes = useMemo(
		() => (applied ? savedFilterChanges(state, baseline, { spellings: spellingsOf(applied) }) : []),
		[applied, state, baseline, spellingsOf]
	);
	const changed = changes.length > 0;
	const mayUpdate = Boolean(applied && !applied.pending && (applied.scope !== FILTER_SCOPE.COLLECTION || canManage));

	// ---- Writing the address ----
	// One patch per event. The stored board and group period agree with it, or
	// the next visit's seed would outvote what was applied.
	const write = useCallback((patch, { keepDefaults = false } = {}) => {
		const next = { ...patch };
		if (next.board) writeStoredBoard(collectionId, { by: next.board, lanes: next.lanes || null });
		if (next.group) {
			onStoredGroup?.(next.group);
			writeStoredItemsGroup(collectionId, next.group);
			// The day is the floor and never written; the stored period says it.
			if (next.group === DEFAULT_GROUP_TOKEN) next.group = undefined;
		}
		update(next, { keepDefaults });
	}, [collectionId, update, onStoredGroup]);

	const apply = useCallback((filter) => {
		// A filter still being saved has a placeholder id: it names nothing yet.
		const id = filter?.pending ? undefined : normalizeId(filter?._id);
		if (id) setAnchor({ key: `${collectionId}:${id}`, state });
		// A stored order is written out loud, so a table layout's own order
		// (which stands in only while nobody chose one) never replaces it.
		const patch = savedFilterMenuPatch(filter, id);
		write(patch, { keepDefaults: Boolean(patch.sort || patch.sort2) });
	}, [collectionId, state, write]);

	const showAll = useCallback(() => {
		update({ saved: undefined, q: undefined, cols: undefined });
	}, [update]);

	const revert = useCallback(() => {
		if (!baseline) return;
		const carriesOrder = Boolean(applied?.sort_field || applied?.sort_then?.length);
		write({ ...savedFilterStatePatch(baseline), saved: normalizeId(applied?._id) }, { keepDefaults: carriesOrder });
	}, [applied, baseline, write]);

	// ---- Saving ----
	const onSaved = useCallback((saved, mode) => {
		// A rename keeps the screen; a save or an Update applies what was stored.
		if (mode !== 'rename') apply(saved);
	}, [apply]);

	const runUpdate = useCallback(async () => {
		if (!applied || !mayUpdate) return;
		setBusy(true);
		try {
			const saved = await patchFilter(applied, draftNow());
			if (saved) apply(saved);
		} catch (error) {
			// A name the person must pick first: the window asks.
			const code = error?.response?.data?.code;
			if (error?.response?.status === 400 && (PICKABLE_REFUSALS.has(code) || PICK_ELEMENT_CODES.has(code))) {
				setNaming({ mode: 'update', filter: applied, error });
			} else {
				message.error(error?.response?.data?.message || 'Could not update the saved filter.');
			}
		} finally {
			setBusy(false);
		}
	}, [applied, mayUpdate, patchFilter, draftNow, apply, message]);

	const confirmDelete = useCallback((filter) => {
		confirmDestructive(modal, {
			title: `Delete "${filter?.name}"?`,
			content: filter?.scope === FILTER_SCOPE.COLLECTION
				? 'It disappears for everyone who uses this collection. History can restore it.'
				: 'Only you can see this saved filter, so only you lose it. History can restore it.',
			okText: 'Delete',
			onOk: async () => {
				await remove(filter);
				// The deleted filter was the one on screen: the screen stays, unnamed.
				if (normalizeId(filter?._id) === normalizeId(savedFilterId)) update({ saved: undefined });
			},
		});
	}, [modal, remove, savedFilterId, update]);

	// Settled once: an id that names nothing listed here reads as All items.
	const readState = deriveDataState({ loading, error: loadError, hasData: loaded });
	const pendingName = Boolean(savedFilterId && !loaded && !loadError);

	// A filter applied by id that the list no longer holds (deleted elsewhere)
	// leaves its anchor behind; drop it so it is taken afresh if it returns.
	useEffect(() => {
		if (!anchorKey && anchor) setAnchor(null);
	}, [anchorKey, anchor]);

	const nameModal = (
		<SavedFilterNameModal
			naming={naming}
			draft={naming ? draftNow() : null}
			fields={fields}
			boardText={boardText}
			onClose={() => setNaming(null)}
			save={save}
			rename={rename}
			update={patchFilter}
			onSaved={onSaved}
		/>
	);

	// The menu carries the name window, so it is mounted wherever the menu is.
	const menu = (
		<>
			<SavedFilterPicker
				collectionId={collectionId}
				filters={filters}
				readState={readState}
				loaded={loaded}
				onRetry={reload}
				applied={applied}
				changes={changes}
				pendingName={pendingName}
				canManage={canManage}
				isMobile={isMobile}
				onApply={apply}
				onAll={showAll}
				onRename={filter => setNaming({ mode: 'rename', filter })}
				onDelete={confirmDelete}
				onPromote={promote}
				onDemote={demote}
			/>
			{nameModal}
		</>
	);

	// Only when there is something to offer: an empty slot draws no row on a phone.
	const canSave = !applied && hasSomethingToSave(state);
	const commit = (applied && changed) || canSave ? (
		<SavedFilterCommit
			applied={applied}
			changed={changed}
			mayUpdate={mayUpdate}
			canSave={canSave}
			busy={busy}
			onUpdate={runUpdate}
			onSaveNew={() => setNaming({ mode: 'save-new' })}
			onRevert={revert}
			onSave={() => setNaming({ mode: 'save' })}
		/>
	) : null;

	return { menu, commit, savedQueries, applied, changes };
}
